import type { Dispatch } from 'redux'
import type { ActionsNetwork } from 'shared/store/actions'
import { fetchPageData } from 'shared/store/actions'
import endpoints from 'shared/store/endpoints'
import type { RootState } from 'shared/store/reducers'
import { getAnalyticsData, updateOptinPush } from 'shared/store/user'
import { closeWebsocket } from 'shared/store/websocket'
import { ws } from 'shared/store/ws'
import type {
    ChangePasswordForm,
    CreditCardInfo,
    Kid,
    OptinDTO,
    OrderRestaurantShortDTO,
    ProfilePage,
    ProfileUserForm,
    RestaurantShortDTO,
    StoredPaymentMethod,
    ChangeEmailForm as UpdateEmailFormType
} from 'shared/types'
import { errors } from 'shared/utils'

type GetState = () => RootState

export type ActionProfileDataResponse = {
    type: 'PAGE_DATA_PROFILE_RESPONSE',
    data: ProfilePage
}

type ActionProfileOptinsResponse = {
    type: 'PROFILE_OPTINS_RESPONSE',
    data: OptinDTO
}

type ActionProfileUpdateError = {
    type: 'PROFILE_UPDATE_ERROR',
    profileUpdateError: string
}

type ActionProfileUpdateSuccess = {
    type: 'PROFILE_UPDATE_SUCCESS',
    profile: ProfileUserForm
}

type ActionResetProfileUpdateSuccess = {
    type: 'PROFILE_RESET_UPDATE_SUCCESS'
}

type ActionDeleteProfileSuccess = {
    type: 'PROFILE_DELETE_SUCCESS'
}

type ActionDeleteProfileError = {
    type: 'PROFILE_DELETE_ERROR'
}

type ActionResetDeleteProfileError = {
    type: 'PROFILE_RESET_DELETE_ERROR'
}

type ActionResetUpdateProfileError = {
    type: 'PROFILE_RESET_UPDATE_ERROR'
}

type ActionEmailUpdateError = {
    type: 'PROFILE_EMAIL_UPDATE_ERROR',
    updateEmailError: string
}

type ActionEmailUpdateSuccess = {
    type: 'PROFILE_EMAIL_UPDATE_SUCCESS'
}

type ActionResetEmailUpdateSuccess = {
    type: 'PROFILE_RESET_EMAIL_UPDATE_SUCCESS'
}

type ActionEmailActivateError = {
    type: 'PROFILE_EMAIL_ACTIVATE_ERROR',
    activateEmailError: string
}

type ActionEmailActivationPathError = {
    type: 'EMAIL_ACTIVATION_PATH_ERROR'
}

type ActionEmailActivateSuccess = {
    type: 'PROFILE_EMAIL_ACTIVATE_SUCCESS',
    email: string
}

type ActionResetEmailActivateSuccess = {
    type: 'PROFILE_RESET_EMAIL_ACTIVATE_SUCCESS'
}

type ActionPasswordUpdateError = {
    type: 'PROFILE_PASSWORD_UPDATE_ERROR',
    updatePasswordError: string
}

type ActionPasswordUpdateSuccess = {
    type: 'PROFILE_PASSWORD_UPDATE_SUCCESS'
}

type ActionResetPasswordUpdateSuccess = {
    type: 'PROFILE_RESET_PASSWORD_UPDATE_SUCCESS'
}

type ActionAddKidError = {
    type: 'PROFILE_ADD_KID_ERROR',
    addKidError: string
}

type ActionAddKidSuccess = {
    type: 'PROFILE_ADD_KID_SUCCESS',
    kids: Array<Kid>
}

type ActionResetAddKidSuccess = {
    type: 'PROFILE_RESET_ADD_KID_SUCCESS'
}

type ActionDeleteKidSuccess = {
    type: 'PROFILE_DELETE_KID_SUCCESS',
    kids: Array<Kid>
}

type ActionDeleteKidError = {
    type: 'PROFILE_DELETE_KID_ERROR'
}

type ActionResetDeleteKidError = {
    type: 'PROFILE_RESET_DELETE_KID_ERROR'
}

type ActionProfileSelectRestaurant = {
    type: 'PROFILE_SELECT_RESTAURANT',
    selectedRestaurant: RestaurantShortDTO
}

type ActionProfileAddRestaurantSuccess = {
    type: 'PROFILE_ADD_RESTAURANT_SUCCESS',
    favRestaurant: RestaurantShortDTO
}

type ActionProfileResetAddRestaurantSuccess = {
    type: 'PROFILE_RESET_ADD_RESTAURANT_SUCCESS'
}

type ActionProfileAddRestaurantError = {
    type: 'PROFILE_ADD_RESTAURANT_ERROR',
    addRestaurantError: string
}

type ActionFetchFavoriteRestaurantSuccess = {
    type: 'PROFILE_FETCH_RESTAURANT_SUCCESS',
    favRestaurant: RestaurantShortDTO
}

/*type ActionDeleteRestaurantSuccess = {
    type: 'PROFILE_DELETE_RESTAURANT_SUCCESS',
    favRestaurants: Array<RestaurantShortDTO>
}*/

type ActionProfileRestaurantSearchResults = {
    type: 'SEARCH_RESULTS',
    restaurants: Array<OrderRestaurantShortDTO>
}

type ActionProfileRestaurantClearSearch = {
    type: 'CLEAR_SEARCH'
}

type ActionHomePageDataResponse = {
    type: 'PAGE_DATA_HOMEPAGE_RESPONSE'
}

type ActionProfileLogout = {
    type: 'LOGOUT'
}

type ActionProfileSigninSend = {
    type: 'SIGNIN_SEND_FORM_SUCCESS'
}

type ActionProfileSigninSocial = {
    type: 'SOCIAL_PENDING'
}

type ActionProfileSignupSend = {
    type: 'SIGNUP_SEND_FORM_SUCCESS'
}

type ActionDeleteCreditCard = {
    type: 'PROFILE_DELETE_CREDIT_CARD_SUCCESS',
    creditCards: CreditCardInfo[]
}

type ActionDeleteCreditCardV2 = {
    type: 'PROFILE_DELETE_CREDIT_CARD_V2_SUCCESS',
    creditCardsV2: StoredPaymentMethod[]
}

type ActionUpdateCarDetails = {
    type: 'UPDATE_CAR_DETAILS',
    carColor: string,
    licensePlateNumber: string,
    licensePlateEnrolment: boolean
}

type ActionUpdateOptins = {
    type: 'UPDATE_OPTINS',
    optins: OptinDTO
}

const fetchProfilePageData = () => {
    return (dispatch: Dispatch<any>) => {
        dispatch(fetchProfileData())
        dispatch(fetchProfileOptins())
    }
}

const fetchProfileData = () => {
    return (dispatch: Dispatch<any>) => {
        dispatch(
            fetchPageData(
                endpoints.kingdom.profile.user,
                'PAGE_DATA_PROFILE_RESPONSE'
            )
        )
    }
}

const fetchProfileOptins = () => {
    return (dispatch: Dispatch<any>) => {
        dispatch(
            fetchPageData(
                endpoints.kingdom.profile.optins,
                'PROFILE_OPTINS_RESPONSE'
            )
        )
    }
}

const updateProfile = (form: ProfileUserForm) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            const profile = { ...form }
            await ws.post(endpoints.kingdom.profile.update, profile)
            dispatch(getAnalyticsData())
            dispatch(actionProfileUpdateSuccess(profile))
            if (profile.optins) {
                dispatch(updateOptinPush(profile.optins.push))
            }
        } catch (error) {
            const { response } = error
            console.error('Error updating profile', response)
            let message = 'form.error.default'
            if (response) {
                const { data } = response
                if (data) {
                    switch (data.message) {
                        case errors.LOGIN_ALREADY_USED:
                            message = 'form.error.login.already.used'
                            break
                        case errors.PHONE_WRONG_FORMAT:
                            message = 'form.error.phone.wrong.format'
                            break
                        case errors.BADGE_ALREADY_EARNED:
                            message = 'form.error.badge.already.earned'
                            break
                        case errors.INVALID_STUDENT_EMAIL:
                            message = 'form.error.invalid.student.email'
                            break
                        case errors.TOO_OLD_FOR_STUDENT:
                            message = 'form.error.too.old.for.student'
                            break
                        case errors.STUDENT_EMAIL_ALREADY_USED:
                            message = 'form.error.student.already.used'
                            break
                        default:
                            break
                    }
                }
            }
            dispatch(actionProfileUpdateError(message))
            throw error
        }
    }
}

const actionUpdateOptins = (optins: OptinDTO): ActionUpdateOptins => ({
    type: 'UPDATE_OPTINS',
    optins
})

const updateProfileOptins = (optins: OptinDTO) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            await ws.post(endpoints.kingdom.profile.update, { optins })
            dispatch(getAnalyticsData())
            dispatch(actionUpdateOptins(optins))
            dispatch(updateOptinPush(optins.push))
        } catch (error) {
            const { response } = error
            console.error('Error updating profile optins', response)
            let message = 'form.error.default'
            if (response) {
                const { data } = response
                if (data) {
                    switch (data.message) {
                        case errors.LOGIN_ALREADY_USED:
                            message = 'form.error.login.already.used'
                            break
                        case errors.PHONE_WRONG_FORMAT:
                            message = 'form.error.phone.wrong.format'
                            break
                        case errors.BADGE_ALREADY_EARNED:
                            message = 'form.error.badge.already.earned'
                            break
                        case errors.INVALID_STUDENT_EMAIL:
                            message = 'form.error.invalid.student.email'
                            break
                        case errors.TOO_OLD_FOR_STUDENT:
                            message = 'form.error.too.old.for.student'
                            break
                        case errors.STUDENT_EMAIL_ALREADY_USED:
                            message = 'form.error.student.already.used'
                            break
                        default:
                            break
                    }
                }
            }
            dispatch(actionProfileUpdateError(message))
            throw error
        }
    }
}

const actionProfileUpdateSuccess = (
    profile: ProfileUserForm
): ActionProfileUpdateSuccess => ({
    type: 'PROFILE_UPDATE_SUCCESS',
    profile
})

const resetProfileUpdateSuccess = (): ActionResetProfileUpdateSuccess => ({
    type: 'PROFILE_RESET_UPDATE_SUCCESS'
})

const actionProfileUpdateError = (
    profileUpdateError: string
): ActionProfileUpdateError => ({
    type: 'PROFILE_UPDATE_ERROR',
    profileUpdateError
})

const updateEmail = (form: UpdateEmailFormType) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            await ws.post(endpoints.kingdom.email.update, {
                ...form
            })
            dispatch(actionUpdateEmailSuccess())
        } catch (error) {
            const { response } = error
            console.error('Error updating email', response)
            let message = 'form.error.default'
            if (response) {
                const { data } = response
                if (data) {
                    switch (data.message) {
                        case errors.INVALID_EMAIL:
                            message = 'signup.form.send.error.email'
                            break
                        case errors.CANNOT_CHANGE_EMAIL:
                            message = 'kingdom.profile.form.email.error'
                            break
                        default:
                            break
                    }
                }
            }
            dispatch(actionUpdateEmailError(message))
        }
    }
}

const actionUpdateEmailSuccess = (): ActionEmailUpdateSuccess => ({
    type: 'PROFILE_EMAIL_UPDATE_SUCCESS'
})

const resetEmailUpdateSuccess = (): ActionResetEmailUpdateSuccess => ({
    type: 'PROFILE_RESET_EMAIL_UPDATE_SUCCESS'
})

const actionUpdateEmailError = (
    updateEmailError: string
): ActionEmailUpdateError => ({
    type: 'PROFILE_EMAIL_UPDATE_ERROR',
    updateEmailError
})

const getUrlParams = (params: any) => {
    return async (dispatch: Dispatch<any>) => {
        if (!params['token']) {
            return false
        } else {
            const email = params['email']
            const token = params['token']
            await dispatch(activateEmail(email, token))
            return true
        }
    }
}

const activateEmail = (email: string, token: string) => {
    return async (dispatch: Dispatch<any>) => {
        if (!token) {
            console.error('URL is invalid: token not found !')
            dispatch(actionEmailActivationPathError())
        } else {
            try {
                await ws.get(endpoints.kingdom.email.activate, {
                    params: { token: token }
                })
                dispatch(resetEmailUpdateSuccess())
                dispatch(actionActivateEmailSuccess(email))
                return true
            } catch (error) {
                console.error('Error activating email', error)
                dispatch(actionActivateEmailError(error))
            }
        }
    }
}

const actionActivateEmailSuccess = (
    email: string
): ActionEmailActivateSuccess => ({
    type: 'PROFILE_EMAIL_ACTIVATE_SUCCESS',
    email
})

const resetEmailActivateSuccess = (): ActionResetEmailActivateSuccess => ({
    type: 'PROFILE_RESET_EMAIL_ACTIVATE_SUCCESS'
})

const actionActivateEmailError = (
    activateEmailError: string
): ActionEmailActivateError => ({
    type: 'PROFILE_EMAIL_ACTIVATE_ERROR',
    activateEmailError
})

const actionEmailActivationPathError = (): ActionEmailActivationPathError => ({
    type: 'EMAIL_ACTIVATION_PATH_ERROR'
})

const updatePassword = (form: ChangePasswordForm) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            await ws.post(endpoints.kingdom.password.update, {
                ...form
            })
            dispatch(actionUpdatePasswordSuccess())
        } catch (error) {
            const { response } = error
            console.error('Error updating password', response)
            let message = 'form.error.default'
            if (response) {
                const { data } = response
                if (data) {
                    switch (data.message) {
                        case errors.WRONG_OLD_PASSWORD:
                            message = 'kingdom.profile.form.password.old.error'
                            break
                        case errors.PASSWORD_WRONG_FORMAT:
                            message = 'form.error.regex.password'
                            break
                        case errors.PASSWORDS_ARE_DIFFERENT:
                            message = 'form.password.update.match.error'
                            break
                        default:
                            break
                    }
                }
            }
            dispatch(actionUpdatePasswordError(message))
        }
    }
}

const actionUpdatePasswordSuccess = (): ActionPasswordUpdateSuccess => ({
    type: 'PROFILE_PASSWORD_UPDATE_SUCCESS'
})

const resetPasswordUpdateSuccess = (): ActionResetPasswordUpdateSuccess => ({
    type: 'PROFILE_RESET_PASSWORD_UPDATE_SUCCESS'
})

const actionUpdatePasswordError = (
    updatePasswordError: string
): ActionPasswordUpdateError => ({
    type: 'PROFILE_PASSWORD_UPDATE_ERROR',
    updatePasswordError
})

const addKid = (form: Kid) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        try {
            const response = await ws.post(endpoints.kingdom.profile.kid, {
                ...form
            })
            const { kids } = getState().kingdomProfilePageState
            kids.push({
                ...form,
                id: response ? response.data.id : ''
            })
            dispatch(actionAddKidSuccess(kids))
            dispatch(getAnalyticsData())
        } catch (error) {
            const { response } = error
            console.error('Error adding kid', response)
            let message = 'form.error.default'
            if (response) {
                const { data } = response
                if (data) {
                    switch (data.message) {
                        case errors.MAX_KIDS_REACHED:
                            message =
                                'kingdom.profile.form.family.add.max.reached'
                            break
                        case errors.CANNOT_PARSE_BIRTHDATE:
                            break
                        default:
                            break
                    }
                }
            }
            dispatch(actionAddKidError(message))
        }
    }
}

const actionAddKidSuccess = (kids: Array<Kid>): ActionAddKidSuccess => ({
    type: 'PROFILE_ADD_KID_SUCCESS',
    kids
})

const actionAddKidError = (addKidError: string): ActionAddKidError => ({
    type: 'PROFILE_ADD_KID_ERROR',
    addKidError
})

const resetKidSuccess = (): ActionResetAddKidSuccess => ({
    type: 'PROFILE_RESET_ADD_KID_SUCCESS'
})

const deleteKid = (id: string) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        try {
            await ws.delete(`${endpoints.kingdom.profile.kid}/${id}`)
            const { kids } = getState().kingdomProfilePageState
            const updatedKids = kids.filter((kid: Kid) => kid && kid.id !== id)
            dispatch(actionDeleteKidSuccess(updatedKids))
            dispatch(getAnalyticsData())
        } catch (error) {
            const { response } = error
            console.error('Error deleting kids', response)
            dispatch(deleteKidError())
        }
    }
}

const actionDeleteKidSuccess = (kids: Array<Kid>): ActionDeleteKidSuccess => ({
    type: 'PROFILE_DELETE_KID_SUCCESS',
    kids
})

const deleteKidError = (): ActionDeleteKidError => ({
    type: 'PROFILE_DELETE_KID_ERROR'
})

const resetDeleteKidError = (): ActionResetDeleteKidError => ({
    type: 'PROFILE_RESET_DELETE_KID_ERROR'
})

const selectRestaurant = (
    selectedRestaurant: RestaurantShortDTO
): ActionProfileSelectRestaurant => ({
    type: 'PROFILE_SELECT_RESTAURANT',
    selectedRestaurant
})

const addRestaurant = (restaurant: RestaurantShortDTO) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            await ws.post(endpoints.kingdom.profile.restaurant, {
                id: restaurant.id
            })
            dispatch(actionAddRestaurantSuccess(restaurant))
            dispatch(getAnalyticsData())
        } catch (error) {
            const { response } = error
            console.error('Error adding restaurant', response)
            let message = 'form.error.default'
            if (response) {
                const { data } = response
                if (data) {
                    switch (data.message) {
                        case errors.RESTAURANT_NOT_FOUND:
                            message = 'form.error.restaurant.not.found'
                            break
                        case errors.RESTAURANT_ALREADY_FAVORITED:
                            message = 'form.error.restaurant.already.favorited'
                            break
                        default:
                            break
                    }
                }
            }
            dispatch(actionAddRestaurantError(message))
        }
    }
}

const actionAddRestaurantSuccess = (
    favRestaurant: RestaurantShortDTO
): ActionProfileAddRestaurantSuccess => ({
    type: 'PROFILE_ADD_RESTAURANT_SUCCESS',
    favRestaurant
})

const fetchFavoriteRestaurant = () => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        try {
            const { token } = getState().userState
            const response = await ws.get(
                endpoints.kingdom.profile.restaurant,
                {
                    headers: token
                        ? { RenewToken: true, Authorization: token }
                        : {}
                }
            )
            if (response) {
                dispatch(actionFetchFavoriteRestaurantSuccess(response.data))
            }
        } catch (error) {
            console.error('Error fetching favorite restaurant', error)
        }
    }
}

const actionFetchFavoriteRestaurantSuccess = (
    favRestaurant: RestaurantShortDTO
): ActionFetchFavoriteRestaurantSuccess => ({
    type: 'PROFILE_FETCH_RESTAURANT_SUCCESS',
    favRestaurant
})

const resetAddRestaurantSuccess = (): ActionProfileResetAddRestaurantSuccess => ({
    type: 'PROFILE_RESET_ADD_RESTAURANT_SUCCESS'
})

const actionAddRestaurantError = (
    addRestaurantError: string
): ActionProfileAddRestaurantError => ({
    type: 'PROFILE_ADD_RESTAURANT_ERROR',
    addRestaurantError
})

/*const deleteRestaurant = (id: string) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        try {
            await ws.delete(`${endpoints.kingdom.profile.restaurant}/${id}`)
            const { favRestaurants } = getState().kingdomProfilePageState
            const updatedFavRestaurants = favRestaurants.filter(
                (r: RestaurantShortDTO) => r && r.id !== id
            )
            dispatch(actionDeleteRestaurantSuccess(updatedFavRestaurants))
            dispatch(getAnalyticsData())
        } catch (error) {
            const { response } = error
            console.error(response)
        }
    }
}

const actionDeleteRestaurantSuccess = (
    favRestaurants: Array<RestaurantShortDTO>
): ActionDeleteRestaurantSuccess => ({
    type: 'PROFILE_DELETE_RESTAURANT_SUCCESS',
    favRestaurants
})*/

const deleteProfile = () => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        try {
            const { orders } = getState().orbState
            await ws.delete(endpoints.kingdom.profile.user)
            if (Object.keys(orders).length === 0) {
                closeWebsocket()
            }
            dispatch(actionDeleteProfileSuccess())
            return true
        } catch (error) {
            const { response } = error
            console.error('Error deleting profile', response)
            dispatch(actionDeleteProfileError())
        }
    }
}

const actionDeleteProfileSuccess = (): ActionDeleteProfileSuccess => ({
    type: 'PROFILE_DELETE_SUCCESS'
})

const actionDeleteProfileError = (): ActionDeleteProfileError => ({
    type: 'PROFILE_DELETE_ERROR'
})

const resetDeleteProfileError = (): ActionResetDeleteProfileError => ({
    type: 'PROFILE_RESET_DELETE_ERROR'
})

const resetUpdateProfileError = (): ActionResetUpdateProfileError => ({
    type: 'PROFILE_RESET_UPDATE_ERROR'
})

const actionDeleteCreditCard = (
    creditCards: CreditCardInfo[]
): ActionDeleteCreditCard => ({
    type: 'PROFILE_DELETE_CREDIT_CARD_SUCCESS',
    creditCards
})

const actionDeleteCreditCardV2 = (
    creditCardsV2: StoredPaymentMethod[]
): ActionDeleteCreditCardV2 => ({
    type: 'PROFILE_DELETE_CREDIT_CARD_V2_SUCCESS',
    creditCardsV2
})

const deleteCreditCard = (id: string) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        try {
            const { creditCards } = getState().kingdomProfilePageState
            await ws.delete(`${endpoints.kingdom.profile.creditCard}/${id}`)
            const updatedCards = creditCards.filter(e => e && e.id !== id)
            dispatch(actionDeleteCreditCard(updatedCards))
        } catch (error) {
            const { response } = error
            console.error('Error deleting credit card', response)
        }
    }
}

const deleteCreditCardV2 = (id: string) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        try {
            const { creditCardsV2 } = getState().kingdomProfilePageState
            await ws.delete(`${endpoints.kingdom.profile.creditCardV2}/${id}`)
            const updatedCards = creditCardsV2.filter(e => e && e.id !== id)
            dispatch(actionDeleteCreditCardV2(updatedCards))
        } catch (error) {
            const { response } = error
            console.error('Error deleting credit card v2', response)
        }
    }
}

const updateCarDetails = (
    newCarColor: string,
    newLicensePlateNumber: string,
    newLicensePlateEnrolment: boolean
) => ({
    type: 'UPDATE_CAR_DETAILS',
    carColor: newCarColor,
    licensePlateNumber: newLicensePlateNumber,
    licensePlateEnrolment: newLicensePlateEnrolment
})

export {
    fetchProfilePageData,
    fetchProfileData,
    updateProfile,
    actionUpdateOptins,
    updateProfileOptins,
    resetProfileUpdateSuccess,
    deleteProfile,
    resetDeleteProfileError,
    resetUpdateProfileError,
    updateEmail,
    resetEmailUpdateSuccess,
    getUrlParams,
    activateEmail,
    resetEmailActivateSuccess,
    updatePassword,
    resetPasswordUpdateSuccess,
    addKid,
    resetKidSuccess,
    deleteKid,
    resetDeleteKidError,
    deleteCreditCard,
    deleteCreditCardV2,
    selectRestaurant,
    addRestaurant,
    resetAddRestaurantSuccess,
    fetchFavoriteRestaurant,
    updateCarDetails
}

export type Action =
    | ActionProfileDataResponse
    | ActionProfileOptinsResponse
    | ActionProfileUpdateSuccess
    | ActionProfileUpdateError
    | ActionResetProfileUpdateSuccess
    | ActionDeleteProfileSuccess
    | ActionDeleteProfileError
    | ActionResetDeleteProfileError
    | ActionResetUpdateProfileError
    | ActionDeleteCreditCard
    | ActionDeleteCreditCardV2
    | ActionProfileLogout
    | ActionProfileSigninSend
    | ActionProfileSigninSocial
    | ActionProfileSignupSend
    | ActionHomePageDataResponse
    | ActionEmailUpdateSuccess
    | ActionEmailUpdateError
    | ActionResetEmailUpdateSuccess
    | ActionEmailActivateSuccess
    | ActionEmailActivateError
    | ActionEmailActivationPathError
    | ActionResetEmailActivateSuccess
    | ActionPasswordUpdateSuccess
    | ActionPasswordUpdateError
    | ActionResetPasswordUpdateSuccess
    | ActionAddKidSuccess
    | ActionAddKidError
    | ActionResetAddKidSuccess
    | ActionDeleteKidSuccess
    | ActionDeleteKidError
    | ActionResetDeleteKidError
    | ActionProfileRestaurantSearchResults
    | ActionProfileRestaurantClearSearch
    | ActionProfileSelectRestaurant
    | ActionProfileAddRestaurantSuccess
    | ActionProfileAddRestaurantError
    | ActionFetchFavoriteRestaurantSuccess
    | ActionProfileResetAddRestaurantSuccess
    | ActionsNetwork
    | ActionUpdateCarDetails
    | ActionUpdateOptins
